import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CardCommon from '@op/components/CardCommon';
import { Col, Row, Text } from 'tea-component';
import '@/pages/opinion/assets/scss/pages/feeds/GameStore.scss';
import { connect, getLocale, useDispatch, useHistory, useIntl } from 'umi';
import {
  commonBarHIcon,
  commonStackedBarHIcon,
  lineIcon,
  pieIcon,
  tableIcon,
} from '@/pages/opinion/constants/chart';
import './steamFeedsReviewsDetailed.scss';
import { transLanguage } from '@op/utils';
import { downloadAction } from '@op/components/DownloadCommon/download-page';
import { useGetSingePageSourceRule } from '@op/hooks/useGetPageSourceRule';
import SortBySelect from '@op/components/FilterBoxCommon/filters/single-select-factory';
import SvgIcon from '@home/components/SvgIcon';
import { DBO_TM, DBO_V, DBO_Y } from '@op/utils/format';
import useFeedsDetail from '@op/pages/gamestore/hooks/useFeedsDetail';
import { goStatsDetailAction } from '@op/pages/stats/Detail';
import { dealwithHoldData, dealwithLanguageTableData } from '@op/pages/SteamPerformance/utils';
import { ReviewsTable } from '@op/pages/SteamPerformance/components/ReviewsTable';
import ChannelsLanguageChart from '@op/pages/Performance/components/ChannelsLanguageChart';
import RealtimeKeywordCard from './RealtimeKeywordCard';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';

const chartHeightMap: any = {
  pie: 310,
  'stacked-common-bar': 312,
  line: 326,
};
function SteamFeedsReviewsDetailed({
  languageLoading,
  language,
  filter,
  isStacked = false,
  statsDetailType = 'steam-performance-keyword-insights',
  statsLanguageDetailType = 'steam-insights-language',
  isRealtime = false,
  hasFeedsBtn = true,
}: any) {
  const local = getLocale();
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  // const { need_brand_health, brandHealth, setBrandHealth } =
  //   useSentimentValue();
  const [selectedLanguage, setSelectedLanguage] = useState({ value: 'all' });
  const [selectedMetrics, setSelectedMetrics] = useState('added');
  const { tableData, languageList } = useMemo(() => {
    const { timeline_of_mentions = [], mentions } = language || {};
    const table = dealwithLanguageTableData(timeline_of_mentions);
    const options = (mentions || []).map((item: any) => item.name);

    return {
      tableData: table,
      languageList: options,
    };
  }, [language]);

  const options = useMemo(() => {
    const list = languageList.map((item: any) => {
      return {
        text: transLanguage(item),
        value: item,
      };
    });

    return [{ text: formatMessage({ id: 'All' }), value: 'all' }, ...list];
  }, [languageList, formatMessage]);

  useEffect(() => {
    setSelectedLanguage({ value: 'all' });
  }, [filter]);

  const keywordFilter = useMemo(() => {
    const { should_return_overview_response, ...restFilter } = filter;
    const newFilter = { ...restFilter, channel: ['steam'] };

    if (selectedLanguage.value === 'all') {
      return newFilter;
    }

    return {
      ...newFilter,
      language: [selectedLanguage.value],
    };
  }, [selectedLanguage, filter]);

  const languageFilter = useMemo(() => {
    return {
      ...filter,
      channel: ['steam'],
    };
  }, [filter]);

  const history = useHistory();
  useEffect(() => {
    if (!filter) return;
    dispatch({
      type: 'opinionPerformance/getRealtimeLanguage',
      payload: {
        ...languageFilter,
        channel: ['steam'],
        channel_type: 'comments',
        // statistics_field: 'channel',
      },
    });
  }, [languageFilter]);

  const [languageChartType, setLanguageChartType] = useState<
    'line' | 'pie' | 'stacked-common-bar' | 'table' | 'common-bar'
  >(isStacked ? 'stacked-common-bar' : 'common-bar');

  const { operations } = useFeedsDetail({ filter, channel: 'steam', hasFeedsBtn });

  const gotoStatsPage = (url: string) => {
    goStatsDetailAction({
      path: `/opinion/Stats/${url}`,
      params: {
        ...filter,
        channel: ['steam'],
        channel_type: 'comments',
      },
      history,
    });
  };

  function transLanguageInfo(languageName: string) {
    if (languageName === 'Others') {
      return local === 'en-US' ? 'Others' : '其他';
    }
    return transLanguage(languageName);
  }

  const languageMetricsOptions = useMemo(() => {
    return [
      { text: formatMessage({ id: 'Reviews' }), value: 'added' },
      // { text: formatMessage({ id: 'All Reviews' }), value: 'all' },
      {
        text: formatMessage({ id: 'Reviews Positive%' }),
        value: 'added_positive_ratio',
      },
      // {
      //   text: formatMessage({ id: 'All Reviews Positive%' }),
      //   value: 'all_positive_ratio',
      // },
    ];
  }, [formatMessage]);

  const finallyDealwithData = useCallback(
    (list: any) => {
      if (selectedMetrics === 'added_positive_ratio') {
        return list.map((item: any) => {
          const value = DBO_V.percentageValue(
            item.positive_mentions,
            item.count,
            6,
          );
          return { ...item, value, count: value };
        });
      }
      return list;
    },
    [selectedMetrics],
  );

  const finallyDealwithTimeline = useCallback((data: any) => {
    if (!isRealtime) {
      return data;
    }
    let { newArr } = data;
    const { timeArr, ...otherInfo } = data;
    newArr = newArr.map((item: any) => {
      return dealwithHoldData(item, timeArr, 2);
    });

    return {
      ...otherInfo,
      newArr,
      timeArr,
    };
  }, []);

  function getChartElement() {
    if (languageChartType === 'table') {
      return (
        <ReviewsTable
          columns={[
            {
              text: 'Date',
              value: 'time',
              width: 150,
              isDate: true,
              align: 'left',
            },
            {
              text: 'Language',
              value: 'language',
              format: (value) => transLanguageInfo(value),
              align: 'left',
              width: 150,
            },
            { text: 'Positive', value: 'positive', width: 150 },
            { text: 'Negative', value: 'negative', width: 150 },
            { text: 'Positive%', value: 'score', isScore: true, width: 150 },
          ]}
          filter={filter}
          records={tableData}
          height={285}
        ></ReviewsTable>
      );
    }

    const isAdded = selectedMetrics === 'added';

    return (
      <ChannelsLanguageChart
        dataType="language"
        data={language}
        height={chartHeightMap[languageChartType]}
        filter={languageFilter}
        type={languageChartType}
        statsDetailType={statsLanguageDetailType}
        showTitle={false}
        finallyDealwithData={finallyDealwithData}
        finallyDealwithTimeline={finallyDealwithTimeline}
        lineProps={{
          showShare: isAdded,
          showSumText: isAdded,
          dataType: isAdded ? 'Reviews' : 'Positive%',
          formatValue: isAdded
            ? (value: number) => DBO_V.reviews(value)
            : (value: number) => DBO_V.ratio_min(value, '-'),
          yAxisFormat: (value: number) => DBO_Y.reviewsPercent(value, !isAdded),
        }}
        leftTitle={
          <div style={{ paddingBottom: 16, marginTop: 4 }}>
            <SortBySelect
              defaultValue={selectedMetrics}
              onChange={(key: string, value: any) => {
                setSelectedMetrics(value);
                buriedBtnPoint('RO_Steam_Language_Metrics', value);
              }}
              defaultParams={{
                options: languageMetricsOptions,
                needFormatMessage: false,
                btnTextPrefix: formatMessage({ id: 'Metrics' }),
                matchButtonWidth: true,
                dealwithButton: (selectValue: string, { textMap }: any) => {
                  return textMap[selectValue];
                },
              }}
            />
          </div>
        }
        className={'steam-feeds-reviews-detailed-language'}
      ></ChannelsLanguageChart>
    );
  }

  function downloadLanguage() {
    downloadAction({
      limit: 150000,
      downLoadParams: filter,
      downloadFunc: 'getOverviewLanguage',
      simpleName: 'Steam Reviews Detailed Language',
      sheetName: 'Steam Reviews Detailed Language',
    });
  }

  const { Go_State_Show } = useGetSingePageSourceRule({
    keys: 'Go_State_Show',
  });

  return (
    <CardCommon
      className="steam-feeds-reviews-detailed-wrapper"
      title={
        <div className="language-distribution-title">
          <SvgIcon className="title-icon-info" type="new-logo"></SvgIcon>
          {formatMessage({ id: 'Language Distribution of Reviews' })}
          <span className="title-tips">
            {formatMessage({
              id: 'Reviews are sourced from the DataBrain crawler',
            })}
          </span>
          <span className="title-tips min-hide">
            {formatMessage({
              id: 'and statistically analyzed by language detection',
            })}
          </span>
          <span className="title-tips">
            ({DBO_TM.timeTitle({ ...filter, link_text: ' ~ ' })})
          </span>
        </div>
      }
      loading={languageLoading}
      operations={operations}
      loadingHeight={388}
      emptyHeight={388}
    >
      <Row gap={40}>
        <Col span={12} className="item-info-col-box">
          <CardCommon
            hasTitleArrow={!Go_State_Show}
            handleTitleOnClick={() => {
              if (Go_State_Show) return;
              gotoStatsPage('Language');
            }}
            title={formatMessage({ id: 'Language' })}
            defaultOperation={languageChartType}
            contentCard
            operations={[
              isStacked ? commonStackedBarHIcon : commonBarHIcon,
              lineIcon,
              pieIcon,
              tableIcon,
            ]}
            handleOperationsOnClick={({ symbol }: any) =>
              setLanguageChartType(symbol)
            }
            hasDownload
            handleDownloadOnClick={downloadLanguage}
            loading={languageLoading}
            empty={
              !Array.isArray(language?.mentions) ||
              language?.mentions.length === 0
            }
            loadingHeight={340}
            emptyHeight={340}
            logBtnId='RO_Steam_Language'
          >
            <div
              className="echart-data"
              style={{ marginTop: languageChartType === 'pie' ? 68 : 10 }}
            >
              <div className="echart-data">{getChartElement()}</div>
            </div>
          </CardCommon>
        </Col>
        <Col span={12} className="item-info-col-box">
          <RealtimeKeywordCard
            filter={keywordFilter}
            curFilter={'total'}
            hasKeywordDownPage={true}
            hasSubTitle={false}
            wordCloudChartHeight={278}
            lineHeight={306}
            tableHeight={256.5}
            cardBeforeElement={
              <SortBySelect
                defaultValue={selectedLanguage.value}
                onChange={(key: string, value: any) => {
                  setSelectedLanguage({ value });
                  buriedBtnPoint('RO_Steam_Language_Keyword', value);
                }}
                defaultParams={{
                  options,
                  needFormatMessage: false,
                  btnTextPrefix: formatMessage({ id: 'Language' }),
                  matchButtonWidth: true,
                  dealwithButton: (selectValue: string, { textMap }: any) => {
                    return textMap[selectValue];
                  },
                }}
              />
            }
            statsDetailType={statsDetailType}
            handleTitleOnClick={() => gotoStatsPage('Keyword')}
            dealwithTimeLineParams={(params: any) => ({
              ...params,
              statistics_field: 'overview',
            })}
            title={formatMessage({ id: 'Keyword' })}
            className={'steam-feeds-reviews-detailed-keyword-card'}
          ></RealtimeKeywordCard>
        </Col>
      </Row>
    </CardCommon>
  );
}

export default connect((state: any) => {
  const { loading, home, opinionCommon, opinionPerformance } = state;

  return {
    userInfo: home.userInfo,
    gameInfo: opinionCommon.gameInfo,
    languageLoading: loading.effects['opinionPerformance/getRealtimeLanguage'],
    language: opinionPerformance.language,
  };
})(SteamFeedsReviewsDetailed);
